import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db945o0kk00l6eltrorg";

const contactChannels = [
  {
    icon: "ri-mail-line",
    label: "邮箱",
    value: "hello@aidesefang.example.com",
  },
  {
    icon: "ri-weibo-line",
    label: "微博",
    value: "@爱的色放电影",
  },
  {
    icon: "ri-wechat-line",
    label: "微信公众号",
    value: "爱的色放电影",
  },
  {
    icon: "ri-time-line",
    label: "回复时间",
    value: "工作日 24 小时内",
  },
];

export default function Contact() {
  usePageMeta({
    title: "联系我们 - 爱的色放电影",
    description:
      "欢迎联系爱的色放电影。留下你的观影感受、影评投稿或合作意向，我们会尽快回复。同时提供邮箱、微博与公众号等联系方式。",
    keywords: "联系我们,影评投稿,反馈建议,爱的色放电影,合作联系",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    const formData = new FormData(form);

    // 防刷蜜罐：正常用户不会填写
    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const content = String(formData.get("content") || "").trim();

    if (!name) {
      setStatus("error");
      setFormError("请填写你的称呼");
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("请填写有效的邮箱地址");
      return;
    }
    if (content.length < 5) {
      setStatus("error");
      setFormError("留言内容至少 5 个字");
      return;
    }
    if (content.length > 500) {
      setStatus("error");
      setFormError("留言内容不能超过 500 字");
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const payload = new URLSearchParams();
      payload.append("name", name);
      payload.append("email", email);
      payload.append("content", content);

      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();

      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试");
    }
  };

  return (
    <div className="w-full px-4 md:px-10 pt-28 md:pt-32 pb-12">
      <div className="max-w-[1440px] mx-auto">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">联系我们</span>
        </nav>

        <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
          联系我们
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-2xl">
          无论是影评投稿、内容建议，还是合作咨询，都欢迎给我们留言。每一条反馈我们都会认真阅读。
        </p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* contact info */}
          <aside className="lg:col-span-1 flex flex-col gap-4">
            {contactChannels.map((c) => (
              <div
                key={c.label}
                className="flex items-start gap-3 rounded-lg bg-background-50 border border-background-200 p-5"
              >
                <span className="w-10 h-10 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                  <i className={`${c.icon} text-xl`}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">{c.label}</p>
                  <p className="mt-0.5 text-sm text-foreground-900 break-words">{c.value}</p>
                </div>
              </div>
            ))}
            <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-5">
              <h2 className="flex items-center gap-2 font-heading font-semibold text-sm text-secondary-900 mb-2">
                <i className="ri-information-line"></i>
                影评投稿说明
              </h2>
              <p className="text-xs text-foreground-600 leading-relaxed">
                投稿请附上影评标题、正文与联系方式，我们会在一周内回复是否采用。优秀投稿将收录至影评专题页面。
              </p>
            </div>
          </aside>

          {/* form */}
          <div className="lg:col-span-2">
            <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
              <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950 mb-1">
                留下你的声音
              </h2>
              <p className="text-sm text-foreground-500 mb-6">
                带 <span className="text-primary-500">*</span> 为必填项
              </p>

              <form id="contact-form-aidesefang" data-readdy-form onSubmit={handleSubmit} noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-2">
                      你的称呼 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="例如：小影"
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-2">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="contact-content" className="block text-sm text-foreground-700 mb-2">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-content"
                    name="content"
                    rows={6}
                    maxLength={500}
                    required
                    placeholder="写下你的观影感受、影评投稿或建议（不超过 500 字）"
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 resize-none leading-relaxed"
                  ></textarea>
                </div>

                {/* anti-spam honeypot */}
                <input
                  type="text"
                  name="website_alt"
                  className="form-guard-alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && formError && (
                  <div className="mt-5 flex items-center gap-2 px-4 py-3 rounded-md bg-primary-50 border border-primary-200 text-sm text-primary-700">
                    <i className="ri-error-warning-line"></i>
                    {formError}
                  </div>
                )}

                {status === "success" && (
                  <div className="mt-5 flex items-center gap-2 px-4 py-3 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-900">
                    <i className="ri-checkbox-circle-line"></i>
                    感谢反馈，稍后会回复
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-6 inline-flex items-center gap-2 px-8 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      提交留言
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}